import React from 'react';
import { Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CustomSelect from 'shared/components/CustomSelect';

import useLanguages from 'hooks/useLanguages';
import { changeDefaultLanguage } from 'redux/actions/topBarActions';

import { DropdownOption } from 'shared/prop-types/ReducerProps';
import { colors, fonts, fontSizes, device } from 'shared/defaultTheme';
import { Checkbox } from 'shared/components/form/CheckBox';

const LanguagesSelector = ({
  mainColor,
  selectedLanguages,
  setSelectedLanguages,
  selectedDefaultLanguage,
  setSelectedDefaultLanguage,
}) => {
  const { languages } = useLanguages(true);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const onChangeLanguage = (event) => {
    const { target } = event;
    const { value } = target;
    setSelectedDefaultLanguage(value);
    i18n.changeLanguage(value);
    dispatch(changeDefaultLanguage(value));
  };

  const onCheck = (currentLanguage) => {
    const checked = selectedLanguages.some(
      (selectedLanguage) => selectedLanguage.value === currentLanguage.value
    );
    if (checked) {
      setSelectedLanguages(
        selectedLanguages.filter(
          (selectedLanguage) => selectedLanguage.value !== currentLanguage.value
        )
      );
      return;
    }
    if (selectedLanguages.length <= 0) {
      setSelectedDefaultLanguage('');
    }
    setSelectedLanguages([...selectedLanguages, currentLanguage]);
  };

  return (
    <LanguagesSelectorWrapper>
      <Col md={5} className="d-flex flex-row align-items-center pl-0">
        <TitleLabel color={mainColor}>{t('general_customizer_default_language')}</TitleLabel>
        <CustomSelect
          className="language-select"
          options={selectedLanguages}
          defaultValue={selectedDefaultLanguage}
          onChange={onChangeLanguage}
        />
      </Col>
      <Col md={7} className="d-flex flex-row align-items-center pr-0 other-languages">
        <TitleLabel color={mainColor}>{t('general_customizer_other_languages')}</TitleLabel>
        <OtherLangaugesWrapper>
          {languages.map((language) => (
            <React.Fragment key={language.value}>
              <Checkbox
                id={`lngChk${language.value}`}
                label={language.name}
                checked={selectedLanguages.some(
                  (selectedLanguage) => selectedLanguage.value === language.value
                )}
                onClick={() => onCheck(language)}
              />
            </React.Fragment>
          ))}
        </OtherLangaugesWrapper>
      </Col>
    </LanguagesSelectorWrapper>
  );
};

LanguagesSelector.defaultProps = {
  mainColor: undefined,
};

LanguagesSelector.propTypes = {
  mainColor: PropTypes.string,
  selectedLanguages: PropTypes.arrayOf(DropdownOption).isRequired,
  setSelectedLanguages: PropTypes.func.isRequired,
  selectedDefaultLanguage: PropTypes.string.isRequired,
  setSelectedDefaultLanguage: PropTypes.func.isRequired,
};

const LanguagesSelectorWrapper = styled(Row)`
  margin-top: 33px;
  width: 100%;
  @media ${device.sm} {
    .other-languages {
      display: block !important;
      margin-left: -15px;
      margin-top: 15px;
    }
  }
  @media ${device.md} {
    .other-languages {
      display: block !important;
      margin-left: -15px;
      margin-top: 15px;
    }
  }
  @media ${device.lg} {
    .other-languages {
      display: block !important;
      margin-left: -15px;
      margin-top: 15px;
    }
  }
  @media ${device.xl} {
    .other-languages {
      display: flex !important;
    }
  }

  .language-select {
    min-height: 38px;
  }
`;

const TitleLabel = styled.h2`
  color: ${({ color }) => color ?? colors.blue} !important;
  font-family: ${fonts.default};
  font-size: ${fontSizes.lg}px;
  font-weight: bold;
  line-height: 21px;
  margin-right: 10px;
`;

const OtherLangaugesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-top: 5px;
  .form__form-check-input {
    margin-right: 8px;
  }
  @media ${device.sm} {
    display: block;
  }
`;

export default LanguagesSelector;
